import { createGlobalState } from "react-hooks-global-state";
import type {
  GlobalState,
  GlobalStateKey,
  GlobalStateValue,
  GlobalStateUpdater,
  UseGlobalStateReturn,
  PopupData,
  AppDownloadPopupData,
  NoticePopupData,
  GlobalPopupData,
} from "./types";

// 初始状态
const initialState: GlobalState = {
  loginModal: null,
  rechargeSavePopupData: null,
  appDownloadPopupData: null,
  rechargeData: null,
  rechargeAppDownloadPopupData: null,
  jumpPathAfterRegister: null,
  noticePopupData: null,
  safariAddHomeScreenShow: false,
  safariAddHomeScreenData: null,
  loginViewFullScreen: false,
  directOpenLotteryGame: false,
  globalPopupData: null,
  popupList: [],
  embedPage: null,
  shownDialogIds: [],
  shareAppBarVisible: true,
  downloadAppBarVisible: true,
  downloadAppPopupVisible: false,
  supportVisible: true,
  eventVisible: true,
  vipBadgeCount: 0,
  isRedirecting: false,
  deviceBlacklisted: false,
};

// 创建全局状态
const { useGlobalState, setGlobalState } = createGlobalState(initialState);

// 内部状态存储，用于在非组件环境中获取状态
const internalState: GlobalState = { ...initialState };

/**
 * 设置全局状态值
 * @param key 状态键
 * @param value 状态值或更新函数
 */
export function setGlobalValue<T extends GlobalStateKey>(
  key: T,
  value: GlobalStateUpdater<T>
): void {
  if (typeof value === "function") {
    // 对于函数类型的更新，我们需要获取当前值
    const currentValue = internalState[key];
    const newValue = (
      value as (prev: GlobalStateValue<T>) => GlobalStateValue<T>
    )(currentValue as GlobalStateValue<T>);
    internalState[key] = newValue;
    setGlobalState(key, newValue);
  } else {
    internalState[key] = value;
    setGlobalState(key, value);
  }
}

/**
 * 获取全局状态值
 * @param key 状态键
 * @returns 状态值
 */
export function getGlobalValue<T extends GlobalStateKey>(
  key: T
): GlobalStateValue<T> {
  // 注意：这个函数不能在React组件外部调用，因为它依赖于全局状态
  // 如果需要获取当前值，应该使用 useGlobalValue hook
  throw new Error(
    `getGlobalValue 不能在组件外部调用。请使用 useGlobalValue('${key}') hook 来获取状态值。`
  );
}

/**
 * 获取全局状态对象（用于工具函数）
 * @returns 当前全局状态
 */
export function getGlobalState(): GlobalState {
  return { ...internalState };
}

/**
 * 使用全局状态的 Hook
 * @param key 状态键
 * @param initValue 初始值（可选）
 * @returns [状态值, 更新函数]
 */
export function useGlobalValue<T extends GlobalStateKey>(
  key: T,
  initValue?: GlobalStateValue<T>
): UseGlobalStateReturn<T> {
  const [value] = useGlobalState(key);
  const currentValue =
    value !== undefined ? value : (initValue as GlobalStateValue<T>);

  const updateValue = (newValue: GlobalStateUpdater<T>) => {
    setGlobalValue(key, newValue);
  };

  return [currentValue, updateValue];
}

// 便捷的状态管理函数

/**
 * 显示/隐藏 Safari 添加到主屏幕提示
 */
export function showSafariAddHomeScreen(flag: boolean): void {
  setGlobalValue("safariAddHomeScreenShow", flag);
}

/**
 * 设置 Safari 添加到主屏幕数据
 */
export function setSafariAddHomeScreenData(data: any): void {
  setGlobalValue("safariAddHomeScreenData", data);
}

/**
 * 显示/隐藏应用下载弹窗
 */
export function showDownloadAppPopup(visible: boolean): void {
  setGlobalValue("downloadAppPopupVisible", visible);
}

/**
 * 显示/隐藏 Support 组件
 */
export function showSupport(visible: boolean): void {
  setGlobalValue("supportVisible", visible);
}

/**
 * 显示/隐藏 Event 组件
 */
export function showEvent(visible: boolean): void {
  setGlobalValue("eventVisible", visible);
}

/**
 * 设置登录弹窗
 */
export function setLoginModal(modal: PopupData | null): void {
  setGlobalValue("loginModal", modal);
}

/**
 * 设置充值数据
 */
export function setRechargeData(data: any): void {
  setGlobalValue("rechargeData", data);
}

/**
 * 设置应用下载弹窗
 */
export function setAppDownloadPopup(data: AppDownloadPopupData | null): void {
  setGlobalValue("appDownloadPopupData", data);
}

/**
 * 设置通知弹窗
 */
export function setNoticePopup(data: NoticePopupData | null): void {
  setGlobalValue("noticePopupData", data);
}

/**
 * 设置全局弹窗
 */
export function setGlobalPopup(data: GlobalPopupData | null): void {
  setGlobalValue("globalPopupData", data);
}

/**
 * 设置跳转路径（注册后）
 */
export function setJumpPathAfterRegister(path: string | null): void {
  setGlobalValue("jumpPathAfterRegister", path);
}

/**
 * 设置登录视图全屏模式
 */
export function setLoginViewFullScreen(fullScreen: boolean): void {
  setGlobalValue("loginViewFullScreen", fullScreen);
}

/**
 * 设置直接打开彩票游戏
 */
export function setDirectOpenLotteryGame(open: boolean): void {
  setGlobalValue("directOpenLotteryGame", open);
}

/**
 * 设置嵌入页面 URL
 */
export function setEmbedPage(url: string | null): void {
  setGlobalValue("embedPage", url);
}

// 导出便捷 hooks
export * from "./hooks";

// getGlobalState 函数已在上面定义并导出

// 导出工具函数
export * from "./utils";

// 导出类型
export type {
  GlobalState,
  GlobalStateKey,
  GlobalStateValue,
  GlobalStateUpdater,
  UseGlobalStateReturn,
  PopupData,
  AppDownloadPopupData,
  NoticePopupData,
  GlobalPopupData,
} from "./types";
